import React from "react";
import { useState, useMemo } from "react";
import ColumnSelectionNode from "./ColumnSelectionNode";
import TreeFilter from "./TreeFilter";
import { getNodeLabel, getNodeClasses, DEFAULT_COLUMNS } from "../utils";

const CheckboxTree = ({
  data,
  selectedColumns,
  setSelectedColumns,
  expandedInitialState = {},
  ontologyDictionary,
}) => {
  const [expanded, setExpanded] = useState(expandedInitialState);
  const [filterText, setFilterText] = useState("");
  const { processedData, parentMap } = useMemo(() => {
    const parentMap = {};

    const processNodes = (node, parentPathKeys = [], parentPathKey = null) => {
      const pathKey = parentPathKey !== null ? `${parentPathKey}/${node.key}` : node.key;
      parentMap[pathKey] = parentPathKeys;

      const classes = getNodeClasses(ontologyDictionary, node.key.split(","));
      const label = getNodeLabel(ontologyDictionary, node, classes);
      const variables = node.variables || [];

      const rawChildren = node.children || [];
      const children = rawChildren
        .map((child) => processNodes(child, [...parentPathKeys, pathKey], pathKey))
        .filter(Boolean);

      if (children.length === 0 && variables.length === 0) {
        return null;
      }

      return {
        ...node,
        pathKey,
        classes,
        label,
        variables,
        children,
      };
    };

    const out = (data || []).map((n) => processNodes(n, [], null)).filter(Boolean);
    return { processedData: out, parentMap };
  }, [data, ontologyDictionary]);

  // Filter nodes based on search text
  const filterNodes = (nodes, searchText) => {
    if (!searchText.trim()) return nodes;

    const lowerSearch = searchText.toLowerCase();

    const nodeMatches = (node) => {
      const labelMatch = node.label?.toLowerCase().includes(lowerSearch);

      if (labelMatch) {
        return node;
      }

      // Check if any variable matches
      const matchingVariables = node.variables?.filter(v =>
        v.toLowerCase().includes(lowerSearch)
      ) || [];
      const variableMatch = matchingVariables.length > 0;

      const filteredChildren = node.children ? filterNodes(node.children, searchText) : [];
      const childMatch = filteredChildren.length > 0;

      if (variableMatch || childMatch) {
        if (matchingVariables.length === 0 && filteredChildren.length === 0) {
          return null;
        }

        return {
          ...node,
          children: filteredChildren,
          variables: matchingVariables
        };
      }
      return null;
    };

    return nodes.map(nodeMatches).filter(Boolean);
  };

  const filteredData = useMemo(() =>
    filterNodes(processedData, filterText),
    [processedData, filterText]
  );

  // Compute the set of keys that should be expanded when a filter is active.
  const expandAllSet = useMemo(() => {
    const set = new Set();
    if (!filterText.trim()) return set;
    const add = (nodes) => {
      nodes.forEach((node) => {
        set.add(node.pathKey ?? node.key);
        if (node.children) add(node.children);
      });
    };
    add(filteredData);
    return set;
  }, [filterText, filteredData]);

  const isExpanded = (key) => {
    if (filterText && filterText.trim()) {
      return expandAllSet.has(key) || !!expanded[key];
    }
    return !!expanded[key];
  };

  const onToggle = (columns, select = true, touchedKeys = []) => {
    setSelectedColumns((prev) => {
      const previous = prev.length === 0 ? DEFAULT_COLUMNS.slice(1) : prev; // auto select default columns excluding LSR no.
      const newSelection = select
        ? [...new Set([...previous, ...columns])]
        : prev.filter((col) => !columns.includes(col));
      return newSelection;
    });

    // If the user selects a node while a filter is active, persist the
    // currently-expanded nodes (including those auto-expanded by the filter)
    // into the user `expanded` state so they remain expanded after clearing the filter.
    if (select && filterText && filterText.trim()) {
      const keysToPersist = (touchedKeys || []).filter((k) => isExpanded(k));
      if (keysToPersist.length > 0) {
        setExpanded((prev) => {
          const copy = { ...prev };
          keysToPersist.forEach((k) => (copy[k] = true));
          return copy;
        });
      }
    }
  };

  return (
    <div className="checkbox-tree">
      <TreeFilter
        filterText={filterText}
        onFilterChange={setFilterText}
        resultsCount={filteredData.length}
      />
      {filteredData.map((node) => (
        <ColumnSelectionNode
          key={node.key}
          node={node}
          isExpanded={isExpanded}
          userExpanded={expanded}
          setExpanded={setExpanded}
          getParentKeys={(k) => parentMap[k] || []}
          selectedColumns={selectedColumns}
          onToggle={onToggle}
          highlightText={filterText}
        />
      ))}
    </div>
  );
};

export default CheckboxTree;
